/**
 * Maps token streams to word-level timing windows. When a TTS audio chunk is
 * absent, the mapper returns a synthetic timing curve so the UI can still
 * progress at a steady pace.
 *
 * The class is dependency-free; consumers feed it tokens and it answers
 * "how many characters of the turn should be visible at this audio playhead?".
 */

import type { ScheduledChunk } from "../../domain/types";

interface TurnState {
  fullText: string;
  charStarts: number[]; // ms offset per character
  totalMs: number;
}

const SYNTH_MS_PER_CHAR = 22; // ~45 cps fallback when no audio is available

export class WordTimingMapper {
  private readonly turns = new Map<string, TurnState>();

  /** Register a finalized text body for a turn (after `turn.end`). */
  setText(turnId: string, text: string): void {
    if (!text) {
      this.turns.set(turnId, { fullText: "", charStarts: [], totalMs: 0 });
      return;
    }
    const charStarts: number[] = new Array(text.length);
    for (let i = 0; i < text.length; i++) {
      charStarts[i] = i * SYNTH_MS_PER_CHAR;
    }
    this.turns.set(turnId, {
      fullText: text,
      charStarts,
      totalMs: text.length * SYNTH_MS_PER_CHAR,
    });
  }

  /** Anchor against a TTS chunk: distribute char timings across the chunk window. */
  applyChunk(turnId: string, chunk: ScheduledChunk, baseMs: number): void {
    const state = this.turns.get(turnId);
    if (!state || state.fullText.length === 0 || chunk.wordTimings.length === 0) return;
    const totalChars = state.fullText.length;
    let cursor = 0;
    for (const wt of chunk.wordTimings) {
      const idx = state.fullText.indexOf(wt.word, cursor);
      if (idx === -1) continue;
      for (let i = idx; i < idx + wt.word.length; i++) {
        if (i >= totalChars) break;
        const fraction = (i - idx) / Math.max(1, wt.word.length);
        state.charStarts[i] = baseMs + wt.tStartMs + fraction * (wt.tEndMs - wt.tStartMs);
      }
      cursor = idx + wt.word.length;
    }
    state.totalMs = Math.max(state.totalMs, baseMs + chunk.wordTimings.at(-1)!.tEndMs);
  }

  /** [0,1] character progress for `turnId` at audio playhead `playheadMs`. */
  getCharProgress(turnId: string, playheadMs: number): number {
    const state = this.turns.get(turnId);
    if (!state || state.fullText.length === 0) return 1;
    if (state.totalMs === 0) return 1;
    const total = state.fullText.length;
    let lo = 0;
    let hi = total - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const t = state.charStarts[mid] ?? 0;
      if (t <= playheadMs) lo = mid + 1;
      else hi = mid - 1;
    }
    const idx = Math.max(0, Math.min(total, lo));
    return idx / total;
  }

  /** Return the substring that should currently be visible. */
  visibleText(turnId: string, playheadMs: number): string {
    const state = this.turns.get(turnId);
    if (!state) return "";
    const progress = this.getCharProgress(turnId, playheadMs);
    return state.fullText.slice(0, Math.ceil(progress * state.fullText.length));
  }

  /** Drop all timing data for `turnId`. */
  clear(turnId: string): void {
    this.turns.delete(turnId);
  }
}

export const wordTimingMapper = new WordTimingMapper();
